import React, { useState, useEffect } from 'react';
import { aiMatchmakingService, UserMatch, PodcastSuggestion } from '../services/aiMatchmakingService';
import { auth, db } from '../firebase';
import { collection, getDocs, addDoc, query, where, orderBy, updateDoc, doc, arrayUnion, arrayRemove, setDoc } from 'firebase/firestore';
import { User } from 'firebase/auth';
import AITestComponent from './AITestComponent';

interface AIMatchmakingProps {
  userProfile: any;
}

const AIMatchmaking: React.FC<AIMatchmakingProps> = ({ userProfile }) => {
  const [matches, setMatches] = useState<UserMatch[]>([]);
  const [podcastSuggestions, setPodcastSuggestions] = useState<PodcastSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<UserMatch | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<PodcastSuggestion | null>(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [matchDetails, setMatchDetails] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'matches' | 'podcasts' | 'stats'>('matches');
  const [testResult, setTestResult] = useState('');
  const [isTestingAI, setIsTestingAI] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const matchesPerPage = 8;

  useEffect(() => {
    if (userProfile?.uid) {
      loadMatches();
      loadPodcastSuggestions();
    }
  }, [userProfile]);

  // New functions for the updated UI
  const switchTab = (tabName: string) => {
    setActiveTab(tabName as 'matches' | 'podcasts' | 'stats');
  };

  const testAI = async () => {
    setIsTestingAI(true);
    setTestResult('');

    try {
      const result = await aiMatchmakingService.testAIIntegration();
      setTestResult(result.success ? 'success' : 'error');
    } catch (error) {
      setTestResult('error');
    } finally {
      setIsTestingAI(false);
    }
  };

  const handleMatchAction = (action: string, cardIndex: number) => {
    const match = matches[cardIndex];
    if (!match) return;

    switch (action) {
      case 'details':
        setSelectedMatch(match);
        setShowMatchModal(true);
        break;
      case 'accept':
        updateMatchStatus(match.id, 'accepted');
        break;
      case 'skip':
        updateMatchStatus(match.id, 'rejected');
        break;
    }
  };

  const loadMatches = async () => {
    try {
      setLoading(true);
      const userMatches = await aiMatchmakingService.getUserMatches(userProfile.uid);
      setMatches(userMatches);
    } catch (error) {
      console.error('Error loading matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPodcastSuggestions = async () => {
    try {
      const suggestions = await aiMatchmakingService.getPodcastSuggestions();
      setPodcastSuggestions(suggestions);
    } catch (error) {
      console.error('Error loading podcast suggestions:', error);
    }
  };

  const findNewMatches = async () => {
    try {
      setLoading(true);
      const newMatches = await aiMatchmakingService.findMatches(userProfile.uid, 5);
      setMatches(prev => [...newMatches, ...prev]);
    } catch (error) {
      console.error('Error finding new matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateNewPodcastSuggestions = async () => {
    try {
      setLoading(true);
      const newSuggestions = await aiMatchmakingService.generatePodcastSuggestions();
      setPodcastSuggestions(prev => [...newSuggestions, ...prev]);
    } catch (error) {
      console.error('Error generating podcast suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const respondToMatch = async (matchId: string, response: 'accepted' | 'rejected') => {
    try {
      await aiMatchmakingService.respondToMatch(matchId, response);
      setMatches(prev => prev.map(match => 
        match.id === matchId ? { ...match, status: response } : match
      ));
      setShowMatchModal(false);
    } catch (error) {
      console.error('Error responding to match:', error);
    }
  };

  const getMatchStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'connected': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Wachtend';
      case 'accepted': return 'Geaccepteerd';
      case 'rejected': return 'Afgewezen';
      case 'contacted': return 'Contact';
      default: return status;
    }
  };

  const getTimelineColor = (timeline: string) => {
    switch (timeline) {
      case 'short': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'long': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimelineText = (timeline: string) => {
    switch (timeline) {
      case 'short': return 'Kort';
      case 'medium': return 'Gemiddeld';
      case 'long': return 'Lang';
      default: return timeline;
    }
  };

  const updateMatchStatus = async (matchId: string, newStatus: 'accepted' | 'rejected' | 'pending' | 'connected') => {
    try {
      const matchRef = doc(db, 'userMatches', matchId);
      await updateDoc(matchRef, { status: newStatus });
      setMatches(prev => prev.map(match => 
        match.id === matchId ? { ...match, status: newStatus } : match
      ));
    } catch (error) {
      console.error('Error updating match status:', error);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFBFC' }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #E5E7EB', 
        position: 'sticky', 
        top: 0, 
        zIndex: 10 
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '16px 24px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            color: '#111827', 
            fontSize: '20px', 
            fontWeight: '600' 
          }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              backgroundColor: '#FF8C42', 
              borderRadius: '6px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              color: 'white', 
              fontWeight: '700', 
              fontSize: '14px' 
            }}>
              AI
            </div>
            AI Matchmaking
          </div>
          <button 
            onClick={findNewMatches}
            disabled={loading}
            style={{ 
              backgroundColor: loading ? '#9CA3AF' : '#FF8C42', 
              color: 'white', 
              padding: '10px 20px', 
              borderRadius: '6px', 
              fontWeight: '500', 
              fontSize: '14px', 
              border: 'none', 
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s'
            }}
          >
            {loading ? 'Zoeken...' : 'Nieuwe Matches Vinden'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', marginBottom: '32px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex' }}>
            {[
              { id: 'matches', label: 'Matches', icon: '🎯' },
              { id: 'podcasts', label: 'Podcast Suggesties', icon: '🎙️' },
              { id: 'stats', label: 'Statistieken', icon: '📊' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => switchTab(tab.id)}
                style={{
                  padding: '16px 24px',
                  borderBottom: activeTab === tab.id ? '2px solid #FF8C42' : '2px solid transparent',
                  color: activeTab === tab.id ? '#FF8C42' : '#6B7280',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 48px' }}>
        {/* Matches Tab */}
        {activeTab === 'matches' && (
          <div>
            <div style={{ marginBottom: '32px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                AI Matches
              </h1>
              <p style={{ color: '#6B7280', fontSize: '14px' }}>
                Beheer connecties en doorloop samen de flow
              </p>
            </div>
            
            {matches.length === 0 ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                padding: '80px 20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.4 }}>🤖</div>
                <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                  Nog geen AI matches
                </h3>
                <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '16px' }}>
                  Vul eerst je profiel in en laat AI je beste connecties vinden
                </p>
                <button
                  onClick={findNewMatches}
                  style={{
                    backgroundColor: '#FF8C42',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontWeight: '500',
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Start AI Matching
                </button>
              </div>
            ) : (
              <>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: '16px',
                  maxWidth: '1200px',
                  margin: '0 auto'
                }}>
                  {matches.slice((currentPage - 1) * matchesPerPage, currentPage * matchesPerPage).map((match, index) => (
                    <div
                      key={match.id}
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        padding: '16px',
                        transition: 'all 0.15s',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleMatchAction('details', index)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: 'linear-gradient(135deg, #FF8C42, #FF9F5A)',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '16px'
                        }}>
                          {match.matchScore}
                        </div>
                        <div style={{
                          backgroundColor: '#FFF7ED',
                          color: '#C2410C',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '10px',
                          fontWeight: '500'
                        }}>
                          {getStatusText(match.status)}
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <h3 style={{ color: '#111827', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                          User #{match.matchedUserId.slice(-6)}
                        </h3>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '2px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '500',
                          backgroundColor: match.matchScore >= 80 ? '#F0FDF4' : '#FFFBEB',
                          color: match.matchScore >= 80 ? '#166534' : '#92400E'
                        }}>
                          <span>{match.matchScore >= 80 ? '✨' : '⭐'}</span>
                          {match.matchScore}%
                        </div>
                      </div>
                      
                      <div style={{
                        backgroundColor: '#F9FAFB',
                        padding: '8px',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        borderLeft: '2px solid #FF8C42'
                      }}>
                        <p style={{ color: '#6B7280', fontSize: '11px', lineHeight: '1.3', margin: 0 }}>
                          {match.matchReasons[0] || 'Geen reden beschikbaar'}
                        </p>
                      </div>

                      <div style={{
                        color: '#9CA3AF',
                        fontSize: '10px',
                        marginBottom: '8px',
                        textAlign: 'center'
                      }}>
                        {new Date(match.createdAt.toDate()).toLocaleDateString('nl-NL')}
                      </div>

                      {match.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMatchAction('accept', index);
                            }}
                            style={{
                              flex: 1,
                              padding: '6px 8px',
                              backgroundColor: '#FF8C42',
                              border: 'none',
                              borderRadius: '4px',
                              fontWeight: '500',
                              fontSize: '11px',
                              color: 'white',
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMatchAction('skip', index);
                            }}
                            style={{
                              flex: 1,
                              padding: '6px 8px',
                              backgroundColor: '#FFF7ED',
                              border: '1px solid #FFEDD5',
                              borderRadius: '4px',
                              fontWeight: '500',
                              fontSize: '11px',
                              color: '#C2410C',
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                          >
                            ✗
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Pagination */}
                {matches.length > matchesPerPage && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    marginTop: '32px'
                  }}>
                    {currentPage > 1 && (
                      <button
                        onClick={() => setCurrentPage(prev => prev - 1)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#F3F4F6',
                          color: '#6B7280',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                      >
                        ← Vorige
                      </button>
                    )}
                    
                    <span style={{
                      padding: '10px 20px',
                      backgroundColor: '#FF8C42',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      Pagina {currentPage} van {Math.ceil(matches.length / matchesPerPage)}
                    </span>
                    
                    {currentPage < Math.ceil(matches.length / matchesPerPage) && (
                      <button
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#FF8C42',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                      >
                        Volgende →
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Podcast Suggesties Tab */}
        {activeTab === 'podcasts' && (
          <div>
            <div style={{ marginBottom: '32px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                Podcast Suggesties
              </h1>
              <p style={{ color: '#6B7280', fontSize: '14px' }}>
                Gepersonaliseerde podcast aanbevelingen op basis van je matches
              </p>
            </div>
            
            {podcastSuggestions.length === 0 ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                padding: '80px 20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.4 }}>🎙️</div>
                <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                  Geen podcast suggesties beschikbaar
                </h3>
                <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '16px' }}>
                  Start met het accepteren van matches om gepersonaliseerde podcast aanbevelingen te krijgen
                </p>
                <button
                  onClick={generateNewPodcastSuggestions}
                  style={{
                    backgroundColor: '#FF8C42',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontWeight: '500',
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Genereer Suggesties
                </button>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(4, 1fr)', 
                gap: '16px',
                maxWidth: '1200px',
                margin: '0 auto'
              }}>
                {podcastSuggestions.map((suggestion) => (
                  <div
                    key={suggestion.id}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      padding: '16px',
                      transition: 'all 0.15s',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSelectedSuggestion(suggestion);
                      setShowSuggestionModal(true);
                    }}
                  >
                    <div style={{ marginBottom: '12px' }}>
                      <h3 style={{ color: '#111827', fontSize: '14px', fontWeight: '600', marginBottom: '4px', lineHeight: '1.3' }}>
                        {suggestion.title}
                      </h3>
                      <span style={{
                        backgroundColor: '#FFF7ED',
                        color: '#C2410C',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: '500'
                      }}>
                        {getStatusText(suggestion.status)}
                      </span>
                    </div>
                    
                    <p style={{ color: '#6B7280', fontSize: '11px', marginBottom: '12px', lineHeight: '1.3' }}>
                      {suggestion.description.length > 80 ? suggestion.description.substring(0, 80) + '...' : suggestion.description}
                    </p>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', fontSize: '10px' }}>
                      <span style={{ color: '#6B7280' }}>{suggestion.estimatedDuration}min</span>
                      <span style={{ color: '#6B7280' }}>{suggestion.currentParticipants}/{suggestion.maxParticipants}</span>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '8px',
                        fontSize: '9px',
                        fontWeight: '500',
                        backgroundColor: getDifficultyColor(suggestion.difficulty).includes('green') ? '#F0FDF4' : 
                                       getDifficultyColor(suggestion.difficulty).includes('yellow') ? '#FFFBEB' : '#FEF2F2',
                        color: getDifficultyColor(suggestion.difficulty).includes('green') ? '#166534' : 
                               getDifficultyColor(suggestion.difficulty).includes('yellow') ? '#92400E' : '#B91C1C'
                      }}>
                        {suggestion.difficulty === 'beginner' && 'B'}
                        {suggestion.difficulty === 'intermediate' && 'G'}
                        {suggestion.difficulty === 'advanced' && 'E'}
                      </span>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {suggestion.topics.slice(0, 2).map((topic, topicIndex) => (
                          <span key={topicIndex} style={{
                            padding: '2px 6px',
                            backgroundColor: '#EFF6FF',
                            color: '#1E40AF',
                            borderRadius: '8px',
                            fontSize: '9px',
                            fontWeight: '500'
                          }}>
                            {topic.length > 12 ? topic.substring(0, 12) + '...' : topic}
                          </span>
                        ))}
                        {suggestion.topics.length > 2 && (
                          <span style={{
                            padding: '2px 6px',
                            backgroundColor: '#F3F4F6',
                            color: '#6B7280',
                            borderRadius: '8px',
                            fontSize: '9px',
                            fontWeight: '500'
                          }}>
                            +{suggestion.topics.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        color: '#9CA3AF',
                        fontSize: '10px'
                      }}>
                        {new Date(suggestion.suggestedDate).toLocaleDateString('nl-NL')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Statistieken Tab */}
        {activeTab === 'stats' && (
          <div>
            <div style={{ marginBottom: '32px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                Statistieken
              </h1>
              <p style={{ color: '#6B7280', fontSize: '14px' }}>
                Overzicht van je matching activiteit en resultaten
              </p>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '24px',
              marginBottom: '32px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                padding: '24px'
              }}>
                <h3 style={{
                  color: '#111827',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🎯</span> Match Statistieken
                </h3>
                {[
                  { label: 'Totaal Matches', value: matches.length.toString(), highlight: true },
                  { label: 'Geaccepteerd', value: matches.filter(m => m.status === 'accepted').length.toString() },
                  { label: 'In Behandeling', value: matches.filter(m => m.status === 'pending').length.toString() },
                  { label: 'Overgeslagen', value: matches.filter(m => m.status === 'rejected').length.toString() },
                  { label: 'Gemiddelde Score', value: matches.length > 0 ? Math.round(matches.reduce((sum, m) => sum + m.matchScore, 0) / matches.length) + '%' : '0%', highlight: true }
                ].map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: index < 4 ? '1px solid #F3F4F6' : 'none'
                  }}>
                    <span style={{ color: '#6B7280', fontSize: '14px' }}>{stat.label}</span>
                    <span style={{
                      fontSize: '14px',
                      color: stat.highlight ? '#FF8C42' : '#111827',
                      fontWeight: stat.highlight ? '700' : '600'
                    }}>
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                padding: '24px'
              }}>
                <h3 style={{
                  color: '#111827',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🎙️</span> Podcast Statistieken
                </h3>
                {[
                  { label: 'Suggesties Ontvangen', value: podcastSuggestions.length.toString() },
                  { label: 'Gepland', value: podcastSuggestions.filter(p => p.status === 'scheduled').length.toString() },
                  { label: 'Voltooid', value: podcastSuggestions.filter(p => p.status === 'completed').length.toString(), highlight: true },
                  { label: 'Gemiddelde Waardering', value: '4.2/5' },
                  { label: 'Totale Luistertijd', value: '24u 15m' }
                ].map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: index < 4 ? '1px solid #F3F4F6' : 'none'
                  }}>
                    <span style={{ color: '#6B7280', fontSize: '14px' }}>{stat.label}</span>
                    <span style={{
                      fontSize: '14px',
                      color: stat.highlight ? '#FF8C42' : '#111827',
                      fontWeight: stat.highlight ? '700' : '600'
                    }}>
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              padding: '24px',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <h3 style={{
                color: '#111827',
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '16px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                <span>🤖</span> AI Integration Test
              </h3>
              <button
                onClick={testAI}
                disabled={isTestingAI}
                style={{
                  width: '100%',
                  backgroundColor: isTestingAI ? '#9CA3AF' : '#FF8C42',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  fontSize: '14px',
                  border: 'none',
                  cursor: isTestingAI ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                {isTestingAI ? 'Verbinding testen...' : 'Test AI Verbinding'}
              </button>
              
              {testResult && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  border: '1px solid',
                  backgroundColor: testResult === 'success' ? '#F0FDF4' : '#FEF2F2',
                  borderColor: testResult === 'success' ? '#BBF7D0' : '#FECACA',
                  color: testResult === 'success' ? '#166534' : '#B91C1C'
                }}>
                  {testResult === 'success' 
                    ? '✅ AI verbinding succesvol! Google AI geïntegreerd en operationeel.'
                    : '❌ AI verbinding gefaald. Systeem draait op fallback algoritme.'
                  }
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Match Details Modal */}
      {showMatchModal && selectedMatch && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            margin: '0 16px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Match Details</h3>
              <button
                onClick={() => setShowMatchModal(false)}
                style={{
                  color: '#6B7280',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #FF8C42, #3B82F6)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '24px',
                  margin: '0 auto 8px'
                }}>
                  {selectedMatch?.matchedUserId?.charAt(0)?.toUpperCase()}
                </div>
                <h4 style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Match #{selectedMatch?.id?.slice(-4)}</h4>
                <p style={{ color: '#6B7280', fontSize: '14px' }}>Score: {selectedMatch?.matchScore}%</p>
              </div>
              
              <div>
                <h5 style={{ fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Waarom deze match?</h5>
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  {selectedMatch?.matchReasons?.map((reason, index) => (
                    <li key={index} style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>• {reason}</li>
                  ))}
                </ul>
              </div>
              
              {selectedMatch?.status === 'pending' && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => respondToMatch(selectedMatch?.id, 'accepted')}
                    style={{
                      flex: 1,
                      backgroundColor: '#10B981',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      fontSize: '14px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    ✅ Accepteren
                  </button>
                  <button
                    onClick={() => respondToMatch(selectedMatch?.id, 'rejected')}
                    style={{
                      flex: 1,
                      backgroundColor: '#6B7280',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      fontSize: '14px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    ❌ Afwijzen
                  </button>
                </div>
              )}
              
              <div style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
                Gemaakt op: {selectedMatch?.createdAt && new Date(selectedMatch.createdAt.toDate()).toLocaleDateString('nl-NL')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Podcast Suggestion Modal */}
      {showSuggestionModal && selectedSuggestion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            margin: '0 16px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Podcast Suggestie</h3>
              <button
                onClick={() => setShowSuggestionModal(false)}
                style={{
                  color: '#6B7280',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <h4 style={{ fontWeight: '600', fontSize: '18px', color: '#111827', marginBottom: '8px' }}>{selectedSuggestion?.title}</h4>
                <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.5' }}>{selectedSuggestion?.description}</p>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Moeilijkheidsgraad:</span>
                  <div style={{ marginTop: '4px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: getDifficultyColor(selectedSuggestion?.difficulty).includes('green') ? '#F0FDF4' : 
                                     getDifficultyColor(selectedSuggestion?.difficulty).includes('yellow') ? '#FFFBEB' : '#FEF2F2',
                      color: getDifficultyColor(selectedSuggestion?.difficulty).includes('green') ? '#166534' : 
                             getDifficultyColor(selectedSuggestion?.difficulty).includes('yellow') ? '#92400E' : '#B91C1C'
                    }}>
                      {selectedSuggestion?.difficulty === 'beginner' && 'Beginner'}
                      {selectedSuggestion?.difficulty === 'intermediate' && 'Gevorderd'}
                      {selectedSuggestion?.difficulty === 'advanced' && 'Expert'}
                    </span>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Duur:</span>
                  <div style={{ color: '#111827', fontSize: '14px', fontWeight: '500', marginTop: '4px' }}>
                    {selectedSuggestion?.estimatedDuration} minuten
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Deelnemers:</span>
                  <div style={{ color: '#111827', fontSize: '14px', fontWeight: '500', marginTop: '4px' }}>
                    {selectedSuggestion?.currentParticipants}/{selectedSuggestion?.maxParticipants}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Voorgestelde datum:</span>
                  <div style={{ color: '#111827', fontSize: '14px', fontWeight: '500', marginTop: '4px' }}>
                    {selectedSuggestion?.suggestedDate && new Date(selectedSuggestion.suggestedDate).toLocaleDateString('nl-NL')}
                  </div>
                </div>
              </div>
              
              <div>
                <h5 style={{ fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Onderwerpen:</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {selectedSuggestion?.topics?.map((topic, index) => (
                    <span key={index} style={{
                      padding: '4px 8px',
                      backgroundColor: '#EFF6FF',
                      color: '#1E40AF',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{
                  flex: 1,
                  backgroundColor: '#FF8C42',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer'
                }}>
                  🎙️ Deelnemen
                </button>
                <button style={{
                  flex: 1,
                  backgroundColor: '#6B7280',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer'
                }}>
                  📅 In Agenda
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIMatchmaking;